import { Add, DeleteOutlined } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import {
  Configuration,
  CustomFieldDefinition,
  ExtendedFieldError,
  FieldDefinition,
} from "product-test-types";
import { checkField, VIEW_NAMES } from "product-test-utils";
import React from "react";
import { v4 as uuidv4 } from "uuid";

import {
  getItemName,
  getPluralOfItemName,
} from "../../utils/get-plural-of-item-name";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../../utils/get-plural-of-project-name";
import { AntSwitch } from "../components/ant-switch";
import { CategoryFields } from "../custom-fields/category-field";
import { CustomizableFormField } from "../custom-fields/customizable-form-field";
import { DefaultFields } from "../custom-fields/default-field";
import { SemaphoreFields } from "../custom-fields/semaphore-fields";
import { StatusFields } from "../custom-fields/status-fields";
import { usePimoForm } from "../pimo-form";

export interface ReportingFieldsFormProps {
  configuration?: Configuration;
  language: SupportedLanguage;
  setUpdatedFieldsFlag: (change: "FieldActivated" | "FieldDeactivated") => void;
}

const fieldTypeTranslationKeyByType = {
  Date: "datatype.date",
  Dropdown: "datatype.select",
  Number: "datatype.number",
  Select: "datatype.select",
  Text: "datatype.text",
  Attachment: "datatype.attachment",
} as const;

const fieldLabelTranslationKeyByFieldName: Record<
  string,
  Record<string, string>
> = {
  achievements: {
    Achievements: "achievements",
  },
  answer: {
    Answer: "answer",
  },
  approvedBudget: {
    "Approved Budget": "approvedBudget",
    Budget: "approvedBudget",
  },
  attachments: {
    Attachments: "attachments",
  },
  category: {
    Category: "category",
  },
  challenges: {
    Challenges: "challenges",
  },
  comment: {
    Comment: "comment",
  },
  completion: {
    Completion: "completion",
    "Degree of Completion": "degreeOfCompletion",
    "Degree of completion": "degreeOfCompletion",
  },
  customId: {
    "Custom ID": "customId",
    ID: "customId",
  },
  description: {
    Description: "description",
  },
  dropdownFieldOne: {
    Category: "category",
    Team: "team",
  },
  endDate: {
    "End Date": "endDate",
  },
  freeTextFieldOne: {
    "Audit Title": "freeTextFieldOne",
  },
  managementSummary: {
    "Management Summary": "managementSummary",
  },
  name: {
    Name: "name",
  },
  nextSteps: {
    "Next Steps": "nextSteps",
  },
  owner: {
    Owner: "owner",
  },
  progress: {
    Progress: "progress",
  },
  programDescription: {
    "Program Description": "programDescription",
  },
  reportTitle: {
    "Report Title": "reportTitle",
  },
  secondCategory: {
    "Audit Type": "auditType",
    Category: "category",
  },
  spentBudget: {
    "Spent Budget": "spentBudget",
  },
  startDate: {
    "Start Date": "startDate",
  },
  status: {
    Status: "status",
  },
  summary: {
    Summary: "summary",
  },
};

const localizeDataFieldType = (language: SupportedLanguage, type: string) =>
  getLocalizedMessage(
    language,
    fieldTypeTranslationKeyByType[
      type as keyof typeof fieldTypeTranslationKeyByType
    ]
  ) || type;

const localizeDataFieldLabel = (
  language: SupportedLanguage,
  field: Pick<FieldDefinition, "fieldName" | "label">
) => {
  const translationKey =
    fieldLabelTranslationKeyByFieldName[field.fieldName]?.[field.label];

  return translationKey
    ? getLocalizedMessage(language, translationKey) || field.label
    : field.label;
};

const DEFAULT_DEGREE_OF_COMPLETION_DEFINITION = [
  {
    name: "Not Started",
    color: "#D9D9D9",
    label: "Choose a label for progress not yet started",
    placeholder: "Not Started",
  },
  {
    name: "In Progress",
    color: "#FFB74D",
    label: "Choose a label for in progress",
    placeholder: "In Progress",
  },
  {
    name: "Completed",
    color: "#4CAF50",
    label: "Choose a label for completed",
    placeholder: "Completed",
  },
];

export const ReportingFieldsForm: PimoReactComponent<
  ReportingFieldsFormProps,
  "define:reporting-data",
  Configuration
> = ({ language, setUpdatedFieldsFlag }) => {
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton?: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const projectName = {
    single: getProjectName(formState?.configuration?.projectName, language),
    plural: getPluralOfProjectName(
      formState?.configuration?.projectName,
      language
    ),
  };
  const itemName = {
    single: getItemName(formState?.configuration?.itemName, language),
    plural: getPluralOfItemName(formState?.configuration?.itemName, language),
  };
  const typeOptions = [
    {
      value: "Text",
      label: getLocalizedMessage(language, "datatype.text"),
    },
    {
      value: "Number",
      label: getLocalizedMessage(language, "datatype.number"),
    },
    {
      value: "Date",
      label: getLocalizedMessage(language, "datatype.date"),
    },
    {
      value: "Select",
      label: getLocalizedMessage(language, "datatype.select"),
    },
  ];

  const [isAccordionExpanded, setIsAccordionExpanded] = React.useState<
    string | false
  >(false);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, newExpanded: boolean) => {
      setIsAccordionExpanded(newExpanded ? panel : false);
    };

  const markFormAsChanged = () => {
    if (formState.enableSaveButton !== true) {
      setValue("enableSaveButton", true);
    }
  };

  const handleAppend = (type: "itemReporting" | "projectReporting") => {
    const newItem: CustomFieldDefinition = {
      id: uuidv4(),
      label: "",
      type: "Text",
      purpose: type,
      options: [{ name: "", color: "#ccc" }],
      enabled: true,
      mandatory: false,
      flag: "create",
    };

    setValue("configuration.customFields", [
      ...(formState?.configuration.customFields ?? []),
      newItem,
    ]);
    markFormAsChanged();
    setUpdatedFieldsFlag("FieldActivated");
    const element = document.getElementById("dummy-to-scroll-into");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "auto",
      }}
    >
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 500,
        }}
      >
        {getLocalizedMessage(language, "reportingData.title") ||
          "Reporting Data"}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: grey[600],
        }}
      >
        {getLocalizedMessage(language, "reportingData.description") ||
          `Reporting data is the data that is regularly updated by the reporting owners. Please select the data fields for the ${projectName.plural} and the ${projectName.plural}.`}
      </Typography>
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 500,
          textAlign: "left",

          width: "100%",
        }}
      >
        {getLocalizedMessage(language, "reportingData.projectLevel") ||
          `Reporting Data on ${projectName} Level`}
      </Typography>
      <Paper
        sx={{
          borderRadius: "12px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        {" "}
        <Grid2
          size={12}
          container
          alignItems="center"
          sx={{
            height: 65,
            p: 2,
            width: "100%",
            boxSizing: "border-box",
            flexWrap: "nowrap",
            columnGap: 1,
          }}
        >
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataField") || "Data Field"}
          </Grid2>
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataType") || "Data Type"}
          </Grid2>
          <Grid2 size="grow" sx={{ minWidth: 0 }}></Grid2>
          <Grid2 size="auto">
            <Button
              variant="outlined"
              onClick={() => handleAppend("projectReporting")}
              startIcon={<Add />}
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                padding: "7px 16px",
                whiteSpace: "nowrap",
                minWidth: "fit-content",
                flexShrink: 0,
              }}
            >
              {getLocalizedMessage(language, "reportingData.addDataField") ||
                "Add Data Field"}
            </Button>
          </Grid2>
        </Grid2>
        {formState.configuration?.fieldsDefinition
          ?.filter((f) => f.purpose === "projectReporting")
          ?.map((field) => (
            <Accordion
              expanded={false}
              onChange={() => {}}
              elevation={0}
              key={field.fieldName + field.purpose}
              disableGutters
              sx={{
                "&:last-of-type": {
                  borderRadius: "12px",
                },
              }}
            >
              <AccordionSummary
                aria-controls="panel1-content"
                id="panel1-header"
                expandIcon={null}
                sx={{
                  "& .MuiAccordionSummary-content": {
                    marginLeft: 0,
                  },
                  cursor: "default",
                  userSelect: "text",
                  width: "98%",
                  height: 65,
                }}
              >
                <Grid2
                  size={12}
                  container
                  alignItems="center"
                  sx={{ height: 65 }}
                >
                  <Grid2
                    size={3}
                    sx={{ fontSize: "14px", overflowWrap: "anywhere" }}
                  >
                    {field.fieldName === "spentBudget"
                      ? `${localizeDataFieldLabel(language, field)} (€)`
                      : localizeDataFieldLabel(language, field)}
                  </Grid2>
                  <Grid2
                    size={3}
                    sx={{ fontSize: "14px", overflowWrap: "anywhere" }}
                  >
                    {localizeDataFieldType(language, field.type)}
                  </Grid2>
                  <Grid2 size={3}>
                    <FormControlLabel
                      labelPlacement="start"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                        },
                      }}
                      control={
                        <Checkbox
                          onClick={(e) => {
                            // stop the click from bubbling to AccordionSummary
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const currentField = {
                              ...field,
                              isMandatory: e.target.checked,
                            };

                            const newFieldsDefinition =
                              formState.configuration?.fieldsDefinition ?? [];
                            const fullArrayIndex =
                              newFieldsDefinition.findIndex(
                                (f) =>
                                  f.fieldName === field.fieldName &&
                                  f.purpose === field.purpose
                              );

                            if (fullArrayIndex !== -1) {
                              newFieldsDefinition[fullArrayIndex] =
                                currentField;
                              setValue(
                                `configuration.fieldsDefinition`,
                                newFieldsDefinition
                              );
                            }
                            markFormAsChanged();
                          }}
                          icon={
                            <span
                              style={{
                                width: 20,
                                height: 20,
                                border: "2px solid",
                                borderColor: grey[700],

                                display: "inline-block",
                                backgroundColor: "#fff",
                              }}
                            />
                          }
                          checkedIcon={
                            <span
                              style={{
                                width: 20,
                                height: 20,

                                border: "2px solid",
                                borderColor: grey[700],
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#fff",
                              }}
                            >
                              <CheckIcon
                                style={{
                                  fontSize: 18,
                                  color: grey[700],
                                }}
                              />
                            </span>
                          }
                          checked={field.isMandatory}
                        />
                      }
                      label={
                        getLocalizedMessage(language, "mandatory") ||
                        "Mandatory"
                      }
                    />
                  </Grid2>
                  <Grid2 size={3} sx={{ fontSize: "14px" }}>
                    <FormControlLabel
                      control={
                        <AntSwitch
                          checked={field.enabled}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const currentField = {
                              ...field,
                              enabled: e.target.checked,
                            };

                            const newFieldsDefinition = [
                              ...(formState.configuration?.fieldsDefinition ??
                                []),
                            ];
                            // Find the correct index in the full array
                            const fullArrayIndex =
                              newFieldsDefinition.findIndex(
                                (f) =>
                                  f.fieldName === field.fieldName &&
                                  f.purpose === field.purpose
                              );

                            if (fullArrayIndex !== -1) {
                              newFieldsDefinition[fullArrayIndex] =
                                currentField;
                              setValue(
                                `configuration.fieldsDefinition`,
                                newFieldsDefinition
                              );
                            }
                            markFormAsChanged();
                            setUpdatedFieldsFlag(
                              e.target.checked
                                ? "FieldActivated"
                                : "FieldDeactivated"
                            );
                          }}
                        />
                      }
                      label={
                        getLocalizedMessage(language, "activate") || "Activate"
                      }
                      labelPlacement="start"
                      sx={{
                        "& .MuiSwitch-thumb": {
                          backgroundColor: grey[300],
                        },
                        "& .MuiSwitch-track": {
                          color: "#6fe255",
                        },
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                        },
                      }}
                    />
                  </Grid2>
                </Grid2>
              </AccordionSummary>
            </Accordion>
          ))}
        {formState.configuration?.customFields
          ?.filter((f) => f.flag !== "delete")
          ?.map((field, index) => {
            if (field.purpose !== "projectReporting") return null;
            return (
              <Accordion
                expanded={isAccordionExpanded === field.id.toString()}
                onChange={handleChange(field.id.toString() as string)}
                elevation={0}
                key={field.id}
                disableGutters
              >
                <AccordionSummary
                  aria-controls="panel1-content"
                  id="panel1-header"
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      marginLeft: 0,
                    },
                    cursor: "default",
                    userSelect: "text",
                    width: "100%",
                    height: 65,
                  }}
                >
                  <Grid2
                    size={12}
                    container
                    alignItems="center"
                    sx={{ height: 65 }}
                  >
                    <Grid2
                      size={3}
                      sx={{ fontSize: "14px", overflowWrap: "anywhere" }}
                    >
                      {field.label}
                    </Grid2>
                    <Grid2
                      size={3}
                      sx={{ fontSize: "14px", overflowWrap: "anywhere" }}
                    >
                      {localizeDataFieldType(language, field.type)}
                    </Grid2>
                    <Grid2 size={3}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                        control={
                          <Checkbox
                            onClick={(e) => {
                              // stop the click from bubbling to AccordionSummary
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                mandatory: e.target.checked,
                              };

                              const newFieldsDefinition =
                                formState.configuration?.customFields ?? [];
                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) => f.id === field.id
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  "configuration.customFields",
                                  newFieldsDefinition
                                );
                              }
                              markFormAsChanged();
                            }}
                            icon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  border: "2px solid",
                                  borderColor: grey[700],

                                  display: "inline-block",
                                  backgroundColor: "#fff",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,

                                  border: "2px solid",
                                  borderColor: grey[700],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <CheckIcon
                                  style={{
                                    fontSize: 18,
                                    color: grey[700],
                                  }}
                                />
                              </span>
                            }
                            checked={field.mandatory}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "mandatory") ||
                          "Mandatory"
                        }
                      />
                    </Grid2>
                    <Grid2 size={2} sx={{ fontSize: "14px" }}>
                      <FormControlLabel
                        control={
                          <AntSwitch
                            checked={field.enabled}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                enabled: e.target.checked,
                              };

                              const newFieldsDefinition = [
                                ...(formState.configuration?.customFields ??
                                  []),
                              ];

                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) => f.id === field.id
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  "configuration.customFields",
                                  newFieldsDefinition
                                );
                              }
                              markFormAsChanged();
                            }}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "activate") ||
                          "Activate"
                        }
                        labelPlacement="start"
                        sx={{
                          "& .MuiSwitch-thumb": {
                            backgroundColor: grey[300],
                          },
                          "& .MuiSwitch-track": {
                            color: "#6fe255",
                          },
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                      />
                    </Grid2>
                    <Grid2 size={1} sx={{ fontSize: "14px" }}>
                      <Button>
                        <DeleteOutlined
                          onClick={() => {
                            const deletedItem =
                              formState.configuration?.customFields?.find(
                                (f) => f.id === field.id
                              );
                            if (deletedItem) {
                              deletedItem.flag = "delete";
                              markFormAsChanged();
                              setUpdatedFieldsFlag("FieldDeactivated");
                            }
                          }}
                        />
                      </Button>
                    </Grid2>
                  </Grid2>
                </AccordionSummary>
                <AccordionDetails>
                  <CustomizableFormField
                    fieldLabel={getLocalizedMessage(language, "fieldLabel")}
                    typeLabel={getLocalizedMessage(language, "type")}
                    optionsLabel={getLocalizedMessage(language, "options")}
                    typeOptions={typeOptions}
                    field={field}
                    onItemUpdate={(field) => {
                      const newCustomFields = [
                        ...(formState.configuration?.customFields ?? []).filter(
                          (f) => f.id !== field.id
                        ),
                        field,
                      ];
                      setValue("configuration.customFields", newCustomFields);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;

                      checkField({
                        language,
                        iterableFieldName: `customFields.{idx}.label`,
                        idx: index,
                        value: field.label,
                        errors: newErrors,
                        tabContainingErrors: VIEW_NAMES[4],
                        setErrors: (errors) => {
                          setValue("validationErrors", errors);
                        },
                      });

                      if (field.type === "Select") {
                        field.options?.forEach((option, optionIndex) => {
                          checkField({
                            language,
                            iterableFieldName: `customFields.{idx}.options.{idx}.name`,
                            idx: [index, optionIndex],
                            value: option.name,
                            errors: newErrors,
                            tabContainingErrors: VIEW_NAMES[4],
                            setErrors: (errors) => {
                              setValue("validationErrors", errors);
                            },
                          });
                        });
                      }

                      markFormAsChanged();
                    }}
                    validationErrors={formState.validationErrors}
                    index={index}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Paper>
      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
        <Button
          variant="outlined"
          onClick={() => handleAppend("projectReporting")}
          startIcon={<Add />}
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            padding: "7px 16px",
            whiteSpace: "nowrap",
            minWidth: "fit-content",
          }}
        >
          {getLocalizedMessage(language, "reportingData.addDataField") ||
            "Add Data Field"}
        </Button>
      </Box>
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 500,
          textAlign: "left",

          width: "100%",
        }}
      >
        {getLocalizedMessage(language, "reportingData.itemLevel") ||
          `Reporting Data on ${itemName.single} Level`}
      </Typography>

      <Paper
        sx={{
          borderRadius: "12px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        <Grid2
          size={12}
          container
          alignItems="center"
          sx={{
            height: 65,
            p: 2,
            width: "100%",
            boxSizing: "border-box",
            flexWrap: "nowrap",
            columnGap: 1,
          }}
        >
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataField") || "Data Field"}
          </Grid2>
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataType") || "Data Type"}
          </Grid2>
          <Grid2 size="grow" sx={{ minWidth: 0 }}></Grid2>
          <Grid2 size="auto">
            <Button
              variant="outlined"
              onClick={() => handleAppend("itemReporting")}
              startIcon={<Add />}
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                padding: "7px 16px",
                whiteSpace: "nowrap",
                minWidth: "fit-content",
                flexShrink: 0,
              }}
            >
              {getLocalizedMessage(language, "reportingData.addDataField") ||
                "Add Data Field"}
            </Button>
          </Grid2>
        </Grid2>
        {formState.configuration?.fieldsDefinition
          ?.filter((f) => f.purpose === "itemReporting")
          ?.map((field) => {
            const isFieldLocked = field.isEditable === false;
            const isDropdown = field.type === "Dropdown";
            return (
              <Accordion
                expanded={isAccordionExpanded === field.fieldName}
                onChange={handleChange(field.fieldName as string)}
                elevation={0}
                key={field.fieldName + field.purpose}
                disableGutters
                sx={{
                  "&:last-of-type": {
                    borderRadius: "12px",
                  },
                }}
              >
                <AccordionSummary
                  aria-controls="panel1-content"
                  id="panel1-header"
                  expandIcon={isDropdown ? <ExpandMoreIcon /> : null}
                  sx={{
                    cursor: "default",
                    userSelect: "text",
                    width: isDropdown ? "100%" : "98%",
                    minHeight: 65,
                    height: "auto",
                  }}
                >
                  <Grid2
                    size={12}
                    container
                    alignItems="center"
                    sx={{ minHeight: 65, py: 1 }}
                  >
                    <Grid2
                      size={3}
                      sx={{ fontSize: "14px", overflowWrap: "anywhere" }}
                    >
                      {field.fieldName === "spentBudget"
                        ? `${localizeDataFieldLabel(language, field)} (€)`
                        : localizeDataFieldLabel(language, field)}
                    </Grid2>
                    <Grid2
                      size={3}
                      sx={{ fontSize: "14px", overflowWrap: "anywhere" }}
                    >
                      {localizeDataFieldType(language, field.type)}
                    </Grid2>
                    <Grid2 size={3}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                        control={
                          <Checkbox
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                isMandatory: e.target.checked,
                              };

                              const newFieldsDefinition =
                                formState.configuration?.fieldsDefinition ?? [];
                              // Find the correct index in the full array
                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) =>
                                    f.fieldName === field.fieldName &&
                                    f.purpose === field.purpose
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  "configuration.fieldsDefinition",
                                  newFieldsDefinition
                                );
                              }
                              markFormAsChanged();
                            }}
                            disabled={isFieldLocked}
                            icon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  border: "2px solid",
                                  borderColor: isFieldLocked
                                    ? grey[400]
                                    : grey[700],

                                  display: "inline-block",
                                  backgroundColor: "#fff",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,

                                  border: "2px solid",
                                  borderColor: isFieldLocked
                                    ? grey[400]
                                    : grey[700],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <CheckIcon
                                  style={{
                                    fontSize: 18,
                                    color: isFieldLocked
                                      ? grey[400]
                                      : grey[700],
                                  }}
                                />
                              </span>
                            }
                            checked={field.isMandatory}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "mandatory") ||
                          "Mandatory"
                        }
                      />
                    </Grid2>
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {isFieldLocked ? (
                        <Chip
                          label={
                            getLocalizedMessage(
                              language,
                              "constantlyActiveField"
                            ) || "Constantly Active Field"
                          }
                          sx={{
                            color: "#008336",
                            fontWeight: 500,
                            backgroundColor: "#e6f9e6",
                          }}
                        ></Chip>
                      ) : (
                        <FormControlLabel
                          control={
                            <AntSwitch
                              checked={field.enabled}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onChange={(e) => {
                                const currentField = {
                                  ...field,
                                  enabled: e.target.checked,
                                };

                                const newFieldsDefinition = [
                                  ...(formState.configuration
                                    ?.fieldsDefinition ?? []),
                                ];
                                // Find the correct index in the full array
                                const fullArrayIndex =
                                  newFieldsDefinition.findIndex(
                                    (f) =>
                                      f.fieldName === field.fieldName &&
                                      f.purpose === field.purpose
                                  );

                                if (fullArrayIndex !== -1) {
                                  newFieldsDefinition[fullArrayIndex] =
                                    currentField;

                                  setValue(
                                    "configuration.fieldsDefinition",
                                    newFieldsDefinition
                                  );
                                }
                                markFormAsChanged();
                              }}
                            />
                          }
                          label={
                            getLocalizedMessage(language, "activate") ||
                            "Activate"
                          }
                          labelPlacement="start"
                          sx={{
                            "& .MuiSwitch-thumb": {
                              backgroundColor: grey[300],
                            },
                            "& .MuiSwitch-track": {
                              color: "#6fe255",
                            },
                            "& .MuiFormControlLabel-label": {
                              fontSize: "14px",
                            },
                          }}
                        />
                      )}
                    </Grid2>
                  </Grid2>
                </AccordionSummary>
                {renderTheCorrectComponent(
                  (field.fieldName === "status"
                    ? "semaphoreStatusDefinition"
                    : field.fieldInConfiguration) as
                    | "statusDefinition"
                    | "degreeOfCompletionDefinition"
                    | "semaphoreStatusDefinition",
                  setValue,
                  formState.configuration,
                  formState.validationErrors,
                  language,
                  field,
                  formState.enableSaveButton
                )}
              </Accordion>
            );
          })}
        {formState.configuration?.customFields
          ?.filter((f) => f.flag !== "delete")
          ?.map((field, index) => {
            if (field.purpose !== "itemReporting") {
              return null;
            }
            return (
              <Accordion
                expanded={isAccordionExpanded === field.id.toString()}
                onChange={handleChange(field.id.toString() as string)}
                elevation={0}
                key={field.id}
                disableGutters
              >
                <AccordionSummary
                  aria-controls="panel1-content"
                  id="panel1-header"
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      marginLeft: 0,
                    },
                    cursor: "default",
                    userSelect: "text",
                    width: "100%",
                    height: 65,
                  }}
                >
                  <Grid2
                    size={12}
                    container
                    alignItems="center"
                    sx={{ height: 65 }}
                  >
                    <Grid2
                      size={3}
                      sx={{ fontSize: "14px", overflowWrap: "anywhere" }}
                    >
                      {field.label}
                    </Grid2>
                    <Grid2
                      size={3}
                      sx={{ fontSize: "14px", overflowWrap: "anywhere" }}
                    >
                      {localizeDataFieldType(language, field.type)}
                    </Grid2>
                    <Grid2 size={3}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                        control={
                          <Checkbox
                            onClick={(e) => {
                              // stop the click from bubbling to AccordionSummary
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                mandatory: e.target.checked,
                              };

                              const newFieldsDefinition =
                                formState.configuration?.customFields ?? [];
                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) => f.id === field.id
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  "configuration.customFields",
                                  newFieldsDefinition
                                );
                              }
                              markFormAsChanged();
                            }}
                            icon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  border: "2px solid",
                                  borderColor: grey[700],

                                  display: "inline-block",
                                  backgroundColor: "#fff",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,

                                  border: "2px solid",
                                  borderColor: grey[700],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <CheckIcon
                                  style={{
                                    fontSize: 18,
                                    color: grey[700],
                                  }}
                                />
                              </span>
                            }
                            checked={field.mandatory}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "mandatory") ||
                          "Mandatory"
                        }
                      />
                    </Grid2>
                    <Grid2 size={2} sx={{ fontSize: "14px" }}>
                      <FormControlLabel
                        control={
                          <AntSwitch
                            checked={field.enabled}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                enabled: e.target.checked,
                              };

                              const newFieldsDefinition = [
                                ...(formState.configuration?.customFields ??
                                  []),
                              ];
                              // Find the correct index in the full array
                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) => f.id === field.id
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  "configuration.customFields",
                                  newFieldsDefinition
                                );
                              }
                              markFormAsChanged();
                            }}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "activate") ||
                          "Activate"
                        }
                        labelPlacement="start"
                        sx={{
                          "& .MuiSwitch-thumb": {
                            backgroundColor: grey[300],
                          },
                          "& .MuiSwitch-track": {
                            color: "#6fe255",
                          },
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                      />
                    </Grid2>
                    <Grid2 size={1} sx={{ fontSize: "14px" }}>
                      <Button>
                        <DeleteOutlined
                          onClick={() => {
                            const deletedItem =
                              formState.configuration?.customFields?.find(
                                (f) => f.id === field.id
                              );
                            if (deletedItem) {
                              deletedItem.flag = "delete";
                              markFormAsChanged();
                            }
                          }}
                        />
                      </Button>
                    </Grid2>
                  </Grid2>
                </AccordionSummary>
                <AccordionDetails>
                  <CustomizableFormField
                    fieldLabel={getLocalizedMessage(language, "fieldLabel")}
                    typeLabel={getLocalizedMessage(language, "type")}
                    optionsLabel={getLocalizedMessage(language, "options")}
                    typeOptions={typeOptions}
                    field={field}
                    onItemUpdate={(field) => {
                      const newCustomFields = [
                        ...(formState.configuration?.customFields ?? []).filter(
                          (f) => f.id !== field.id
                        ),
                        field,
                      ];
                      setValue("configuration.customFields", newCustomFields);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;

                      checkField({
                        language,
                        iterableFieldName: `customFields.{idx}.label`,
                        idx: index,
                        value: field.label,
                        errors: newErrors,
                        tabContainingErrors: VIEW_NAMES[4],
                        setErrors: (errors) => {
                          setValue("validationErrors", errors);
                        },
                      });

                      if (field.type === "Select") {
                        field.options?.forEach((option, optionIndex) => {
                          checkField({
                            language,
                            iterableFieldName: `customFields.{idx}.options.{idx}.name`,
                            idx: [index, optionIndex],
                            value: option.name,
                            errors: newErrors,
                            tabContainingErrors: VIEW_NAMES[4],
                            setErrors: (errors) => {
                              setValue("validationErrors", errors);
                            },
                          });
                        });
                      }
                      markFormAsChanged();
                    }}
                    validationErrors={formState.validationErrors}
                    index={index}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Paper>
      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
        <Button
          variant="outlined"
          onClick={() => handleAppend("itemReporting")}
          startIcon={<Add />}
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            padding: "7px 16px",
            whiteSpace: "nowrap",
            minWidth: "fit-content",
          }}
        >
          {getLocalizedMessage(language, "reportingData.addDataField") ||
            "Add Data Field"}
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 830px)",
          flexGrow: 1,
        }}
      ></Box>
    </Box>
  );
};

const renderTheCorrectComponent = (
  type:
    | "degreeOfCompletionDefinition"
    | "statusDefinition"
    | "semaphoreStatusDefinition"
    | "answerDefinition",

  setValue: (key: string, value: unknown) => void,
  configuration: Configuration | undefined,
  validationErrors: Record<string, ExtendedFieldError>,
  language: SupportedLanguage,
  field: FieldDefinition & {
    purpose:
      "projectReporting" | "itemReporting" | "projectBasic" | "itemBasic";
  },
  enableSaveButton?: boolean
) => {
  const degreeOfCompletionDefinitionEntries = configuration
    ?.degreeOfCompletionDefinition?.length
    ? configuration.degreeOfCompletionDefinition
    : DEFAULT_DEGREE_OF_COMPLETION_DEFINITION;

  switch (type) {
    case "statusDefinition":
      return (
        <AccordionDetails>
          <StatusFields
            onChange={(val) => {
              setValue(`configuration.statusDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              // remove existing statusDefinition.* errors
              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("statusDefinition.")) delete newErrors[k];
              });

              val.forEach((item, index) => {
                checkField({
                  language,
                  iterableFieldName: `statusDefinition.{idx}.name`,
                  idx: index,
                  value: item.name,
                  errors: newErrors,
                  tabContainingErrors: VIEW_NAMES[4],
                  setErrors: (errors) => {
                    setValue("validationErrors", errors);
                  },
                });
              });

              setValue("validationErrors", newErrors);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            title="Define which status the tracking item can assume."
            initialEntries={configuration?.statusDefinition ?? []}
            type="statusDefinition"
            validationErrors={validationErrors}
            language={language}
          />
        </AccordionDetails>
      );
    case "answerDefinition":
      return (
        <AccordionDetails>
          <CategoryFields
            baseFieldName="answerDefinition"
            initialEntries={configuration?.answerDefinition ?? []}
            onEntriesChange={(val) => {
              setValue(`configuration.answerDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              val.forEach((item, index) => {
                checkField({
                  language,
                  iterableFieldName: `answerDefinition.{idx}.name`,
                  idx: index,
                  value: item.name,
                  errors: newErrors,
                  tabContainingErrors: VIEW_NAMES[4],
                  setErrors: (errors) => {
                    setValue("validationErrors", errors);
                  },
                });
              });

              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            fieldName={field.label}
            onFieldNameChange={(name) => {
              const indexOfField =
                configuration?.fieldsDefinition?.findIndex(
                  (f) =>
                    f.fieldName === field.fieldName &&
                    f.purpose === field.purpose
                ) ?? 0;
              const newFieldsDefinition = configuration?.fieldsDefinition ?? [];
              newFieldsDefinition[indexOfField] = {
                ...newFieldsDefinition[indexOfField],
                label: name,
              };
              setValue(`configuration.fieldsDefinition`, newFieldsDefinition);

              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            validationErrors={validationErrors}
          />
        </AccordionDetails>
      );

    case "degreeOfCompletionDefinition":
      return (
        <AccordionDetails>
          <StatusFields
            enableColorPicker={true}
            onChange={(val) => {
              setValue(`configuration.degreeOfCompletionDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              val.forEach((item, index) => {
                checkField({
                  language,
                  iterableFieldName: `degreeOfCompletionDefinition.{idx}.name`,
                  idx: index,
                  value: item.name,
                  errors: newErrors,
                  tabContainingErrors: VIEW_NAMES[4],
                  setErrors: (errors) => {
                    setValue("validationErrors", errors);
                  },
                });
              });

              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            title={
              getLocalizedMessage(language, "statusFields.statusTitle") ||
              "Define which status the tracking item can assume."
            }
            initialEntries={degreeOfCompletionDefinitionEntries}
            type="degreeOfCompletionDefinition"
            validationErrors={validationErrors}
            language={language}
          />
        </AccordionDetails>
      );
    case "semaphoreStatusDefinition":
      return (
        <AccordionDetails>
          <SemaphoreFields
            key={configuration?.statusReportingLevel ?? "overall"}
            initialEntries={configuration?.semaphoreStatusDefinition ?? []}
            initialReportingLevel={configuration?.statusReportingLevel}
            language={language}
            onEntriesChange={(data) => {
              setValue(`configuration.statusReportingLevel`, data.level);

              const statusFieldIndex =
                configuration?.fieldsDefinition?.findIndex(
                  (entry) =>
                    entry.fieldName === "status" &&
                    entry.purpose === field.purpose
                ) ?? -1;

              if (statusFieldIndex >= 0) {
                const nextFieldsDefinition = [
                  ...(configuration?.fieldsDefinition ?? []),
                ];
                nextFieldsDefinition[statusFieldIndex] = {
                  ...nextFieldsDefinition[statusFieldIndex],
                  fieldInConfiguration:
                    data.level === "category"
                      ? "semaphoreStatusDefinition"
                      : "statusDefinition",
                };
                setValue(
                  "configuration.fieldsDefinition",
                  nextFieldsDefinition
                );
              }

              if (data.level === "category") {
                setValue(
                  `configuration.semaphoreStatusDefinition`,
                  data.entries
                );
              }

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("semaphoreStatusDefinition.")) {
                  delete newErrors[k];
                }
              });

              if (data.level === "category") {
                data.entries.forEach((item, index) => {
                  checkField({
                    language,
                    iterableFieldName: `semaphoreStatusDefinition.{idx}.name`,
                    idx: index,
                    value: item.name,
                    errors: newErrors,
                    tabContainingErrors: VIEW_NAMES[4],
                    setErrors: (errors) => {
                      setValue("validationErrors", errors);
                    },
                  });
                });
              }

              setValue("validationErrors", newErrors);

              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            validationErrors={validationErrors}
            initialStatusEntries={configuration?.statusDefinition ?? []}
            onStatusEntriesChange={(val) => {
              setValue(`configuration.statusDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              // remove existing statusDefinition.* errors
              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("statusDefinition.")) {
                  delete newErrors[k];
                }
                if (k.startsWith("semaphoreStatusDefinition.")) {
                  delete newErrors[k];
                }
              });

              val.forEach((item, index) => {
                checkField({
                  language,
                  iterableFieldName: `statusDefinition.{idx}.name`,
                  idx: index,
                  value: item.name,
                  errors: newErrors,
                  tabContainingErrors: VIEW_NAMES[4],
                  setErrors: (errors) => {
                    setValue("validationErrors", errors);
                  },
                });
              });

              setValue("validationErrors", newErrors);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
          ></SemaphoreFields>
        </AccordionDetails>
      );
    default:
      return (
        <AccordionDetails>
          <DefaultFields />
        </AccordionDetails>
      );
  }
};
